import type { BackupRestoreResponse } from "@api-client";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { useEffect, useRef, useState } from "react";
import {
  exportBackup,
  type LegalEntity,
  listLegalEntities,
  listOrganizations,
  restoreBackup,
} from "@/lib/api";
import { useSession } from "@/lib/session";
import {
  OrganizationNavigation,
  organizationViewHref,
  type ConsoleOrganizationOption,
} from "@/features/organizations/OrganizationNavigation";

type OrganizationOption = ConsoleOrganizationOption;

type PageState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | {
      status: "loaded";
      organizationId: string;
      organizationName: string;
      organizations: OrganizationOption[];
      entities: LegalEntity[];
    };

interface FailureState {
  message: string;
  retry: "check" | null;
}

function toErrorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : String(cause);
}

function newIdempotencyKey() {
  try {
    return crypto.randomUUID();
  } catch {
    return `restore-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

export function OrganizationDataPage({
  organizationId,
}: {
  organizationId: string;
}) {
  const { state } = useSession();
  const userId = state.status === "authenticated" ? state.user.id : null;
  const [page, setPage] = useState<PageState>({ status: "loading" });
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [exporting, setExporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const [ready, setReady] = useState(false);
  const snapshotRef = useRef<unknown>(null);
  const idempotencyKeyRef = useRef("");
  const [preview, setPreview] = useState<BackupRestoreResponse | null>(null);
  const [applied, setApplied] = useState<BackupRestoreResponse | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FailureState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (state.status !== "authenticated") return;
    let cancelled = false;
    setPage({ status: "loading" });
    setNotice(null);
    void (async () => {
      try {
        const organizations = await listOrganizations();
        if (cancelled) return;
        const organization = organizations.find(
          (candidate) => candidate.id === organizationId,
        );
        if (!organization) {
          setPage({
            status: "error",
            message: "You do not have access to this organization.",
          });
          return;
        }
        const entities = await listLegalEntities(organization.id);
        if (!cancelled) {
          setPage({
            status: "loaded",
            organizationId: organization.id,
            organizationName: organization.name,
            organizations: organizations.map((item) => ({
              id: item.id,
              name: item.name,
              canManageSecrets: item.can_manage_secrets,
            })),
            entities,
          });
          setSelectedEntityId(
            entities.find((entity) => entity.active)?.id ?? "",
          );
        }
      } catch {
        if (!cancelled) {
          setPage({
            status: "error",
            message:
              "This organization's data could not be loaded. Please try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, state.status, userId]);

  function resetRestore() {
    setFileName("");
    setReady(false);
    snapshotRef.current = null;
    idempotencyKeyRef.current = "";
    setPreview(null);
    setApplied(null);
    setConfirmed(false);
    setFailure(null);
    setNotice(null);
  }

  function selectEntity(entityId: string) {
    setSelectedEntityId(entityId);
    resetRestore();
  }

  async function download() {
    if (page.status !== "loaded" || !selectedEntityId) return;
    setExporting(true);
    setFailure(null);
    setNotice(null);
    try {
      await exportBackup({
        organization_id: page.organizationId,
        legal_entity_id: selectedEntityId,
      });
      setNotice(
        "Snapshot downloaded. It contains the chart of accounts, journals, and template metadata for this legal entity — never credentials or tokens.",
      );
    } catch (cause) {
      setFailure({ message: toErrorMessage(cause), retry: null });
    } finally {
      setExporting(false);
    }
  }

  async function chooseFile(file: File) {
    setFailure(null);
    setNotice(null);
    setPreview(null);
    setApplied(null);
    setConfirmed(false);
    snapshotRef.current = null;
    idempotencyKeyRef.current = "";
    setReady(false);
    setFileName(file.name);
    try {
      const parsed: unknown = JSON.parse(await file.text());
      snapshotRef.current = parsed;
      idempotencyKeyRef.current = newIdempotencyKey();
      setReady(true);
    } catch {
      setFailure({
        message: "That file is not a backup snapshot (invalid JSON).",
        retry: null,
      });
    }
  }

  async function check() {
    if (page.status !== "loaded" || !selectedEntityId) return;
    if (snapshotRef.current === null) return;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    setApplied(null);
    try {
      const result = await restoreBackup(
        {
          organization_id: page.organizationId,
          legal_entity_id: selectedEntityId,
        },
        {
          snapshot: snapshotRef.current,
          dry_run: true,
          idempotency_key: idempotencyKeyRef.current,
        },
      );
      setPreview(result);
      setNotice(
        result.valid === false
          ? null
          : "Snapshot checked. Confirm below to replay it into this legal entity's books.",
      );
      if (result.valid !== false) {
        idempotencyKeyRef.current = newIdempotencyKey();
      }
    } catch (cause) {
      setFailure({ message: toErrorMessage(cause), retry: "check" });
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (page.status !== "loaded" || !selectedEntityId) return;
    if (snapshotRef.current === null || !confirmed) return;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    try {
      const result = await restoreBackup(
        {
          organization_id: page.organizationId,
          legal_entity_id: selectedEntityId,
        },
        {
          snapshot: snapshotRef.current,
          dry_run: false,
          idempotency_key: idempotencyKeyRef.current,
        },
      );
      setApplied(result);
      setPreview(null);
      setConfirmed(false);
      if (result.applied) {
        snapshotRef.current = null;
        setReady(false);
        setNotice(
          result.reason === "already_applied"
            ? "That snapshot was already restored."
            : "Snapshot restored. Replayed journals are drafts.",
        );
      }
    } catch (cause) {
      setFailure({ message: toErrorMessage(cause), retry: null });
    } finally {
      setBusy(false);
    }
  }

  if (page.status === "loading") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10 text-sm text-[var(--muted-foreground)]">
        Loading organization data...
      </main>
    );
  }

  if (page.status === "error") {
    return (
      <main className="mx-auto max-w-5xl px-6 py-10">
        <p role="alert" className="text-sm text-[var(--danger)]">
          {page.message}
        </p>
      </main>
    );
  }

  const activeEntities = page.entities.filter((entity) => entity.active);
  const entitiesHref = organizationViewHref(page.organizationId, "entities");

  return (
    <OrganizationNavigation
      organizationId={page.organizationId}
      organizations={page.organizations}
      onOrganizationChange={(nextOrganizationId) => {
        if (nextOrganizationId === page.organizationId) return;
        window.location.assign(
          organizationViewHref(nextOrganizationId, "data"),
        );
      }}
      view="data"
    >
      <div className="space-y-8">
        <PageHeader
          title="Data"
          description={
            page.organizationName
              ? `${page.organizationName} · Snapshots are per legal entity and never include credentials or tokens.`
              : "Snapshots are per legal entity and never include credentials or tokens."
          }
        />

        <section
          aria-labelledby="organization-data-entity"
          className="surface grid gap-5 rounded-2xl p-5"
        >
          <div className="grid max-w-md gap-1 text-sm font-medium">
            <label htmlFor="organization-data-entity">Legal entity</label>
            <Select
              id="organization-data-entity"
              aria-label="Legal entity"
              onChange={selectEntity}
              options={activeEntities.map((entity) => ({
                value: entity.id,
                label: entity.name,
              }))}
              value={
                activeEntities.some(
                  (entity) => entity.id === selectedEntityId,
                )
                  ? selectedEntityId
                  : (activeEntities[0]?.id ?? "")
              }
            />
          </div>

          {activeEntities.length === 0 ? (
            <div className="grid gap-2 text-sm">
              <p className="text-[var(--muted-foreground)]">
                No active legal entities are available in this organization.
              </p>
              <p>
                <a className="font-medium text-[var(--action)] underline" href={entitiesHref}>
                  Add or activate a legal entity
                </a>{" "}
                before exporting or restoring books.
              </p>
            </div>
          ) : (
            <>
              <section aria-label="Export a snapshot" className="grid min-w-0 gap-3">
                <h2 className="text-sm font-semibold">Export</h2>
                <p className="text-sm text-[var(--muted-foreground)]">
                  Download a restorable snapshot of this legal entity's books:
                  chart of accounts, every journal entry, and
                  document-template metadata. Credentials, tokens, and
                  sessions are never exported.
                </p>
                <div>
                  <Button
                    type="button"
                    onClick={() => void download()}
                    disabled={exporting || !selectedEntityId}
                  >
                    {exporting ? "Preparing…" : "Export snapshot"}
                  </Button>
                </div>
              </section>

              <section aria-label="Restore a snapshot" className="grid min-w-0 gap-3">
                <h2 className="text-sm font-semibold">Restore</h2>
                <p className="text-sm text-[var(--muted-foreground)]">
                  Check a snapshot before restoring it. Restoring replays its
                  journals as drafts and creates missing accounts and
                  templates — it never replaces or deletes what is already
                  here.
                </p>
                <label
                  htmlFor="organization-data-snapshot"
                  className="grid gap-1 text-xs font-medium"
                >
                  Snapshot file (.json)
                  <input
                    id="organization-data-snapshot"
                    type="file"
                    accept=".json,application/json"
                    className="input"
                    disabled={busy}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void chooseFile(file);
                    }}
                  />
                </label>
                {ready ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void check()}
                      disabled={busy}
                    >
                      {busy ? "Checking…" : "Check file"}
                    </Button>
                    <span className="min-w-0 break-all text-xs text-[var(--muted-foreground)]">
                      {fileName}
                    </span>
                  </div>
                ) : null}

                {preview && preview.valid === false ? (
                  <div className="surface rounded-xl border border-[var(--status-danger-foreground)] p-4 text-sm">
                    <p className="font-medium">
                      This snapshot cannot be restored:
                    </p>
                    <ul className="mt-2 grid gap-1 text-xs">
                      {(preview.errors ?? []).map((message, index) => (
                        <li key={`${message}-${index}`}>{message}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {preview && preview.valid !== false ? (
                  <div className="surface grid gap-3 rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
                    <p>
                      Ready to restore {preview.counts?.chart_accounts ?? 0}{" "}
                      account(s), {preview.counts?.journal_entries ?? 0} journal
                      entr(ies), and {preview.counts?.document_templates ?? 0}{" "}
                      template(s).
                    </p>
                    <label className="flex items-start gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        disabled={busy}
                        onChange={(event) =>
                          setConfirmed(event.target.checked)
                        }
                      />
                      I understand journals replay as drafts and existing
                      records are left untouched.
                    </label>
                    <div>
                      <Button
                        type="button"
                        onClick={() => void apply()}
                        disabled={busy || !confirmed}
                      >
                        {busy ? "Restoring…" : "Restore snapshot"}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {applied?.applied ? (
                  <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
                    Restored: {applied.accounts_created ?? 0} account(s),{" "}
                    {applied.journals_created?.length ?? 0} draft journal(s),{" "}
                    {applied.templates_created ?? 0} template(s).
                  </output>
                ) : null}
              </section>
            </>
          )}

          {failure ? (
            <div role="alert" className="grid min-w-0 gap-2">
              <p className="break-words text-sm text-[var(--status-danger-foreground)]">
                {failure.message}
              </p>
              {failure.retry === "check" ? (
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void check()}
                    disabled={busy}
                  >
                    {busy ? "Checking…" : "Retry check"}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {notice ? (
            <p className="text-sm text-[var(--muted-foreground)]">{notice}</p>
          ) : null}
        </section>
      </div>
    </OrganizationNavigation>
  );
}
